import { Card, Flex, Layout, Spin, Statistic, Typography } from "antd"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import ReactCodeInput from "react-verification-code-input"

import Images from "../Images"
import ButtonComponent from "../components/Button"
import LoginHeader from "../components/LoginHeader"
import PrivacyPolicy from "../components/PrivacyPolicy"
import { generateDocumentTitle } from "../constants/document-title"
import { type RootState } from "../store"
import { getResponseMessage } from "../utils/handlers/get-response-message"
import utils from "../utils/utils"
import { authErrorReset } from "./LoginPage/redux/auth.slice"
import {
  checkPassword as checkPasswordThunk,
  confirmPassword as confirmPasswordThunk
} from "./LoginPage/redux/auth.thunks"

const { Title, Paragraph } = Typography
const { Countdown } = Statistic

function VerifyCodePage({
  error,
  fetching,
  resetError,
  userInfo,
  confirmPassword,
  checkPassword,
  isConfirmEmail = false
}: ConnectedProps<typeof connector> & { isConfirmEmail?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location

  const { t } = useTranslation()

  const [isComplete, setIsComplete] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [children, setChildren] = useState<HTMLCollection | undefined | null>(
    null
  )

  const [resendFetching, setResendFetching] = useState(false)
  const [deadline, setDeadline] = useState<number | null>(null)

  const [errorText, setErrorText] = useState<string | null>(null)

  const email = isConfirmEmail ? userInfo?.email : state

  const content = isConfirmEmail
    ? {
        title: "Confirm email",
        text: "A 6-digit code has been sent your email",
        btn: "Continue"
      }
    : {
        title: "Reset password",
        text: "We’ve sent you a 6-digit code to your email",
        btn: "Confirm"
      }

  useEffect(() => {
    generateDocumentTitle("Verify Email")

    const data =
      document?.getElementsByClassName("code-block")?.[0]?.firstElementChild
        ?.children

    setChildren(data)
  }, [])

  useEffect(() => {
    if (children) {
      initCodeBlock(children)
    }
  }, [children])

  useEffect(() => {
    if (error) {
      const message = getResponseMessage(error) || "Something went wrong"

      setErrorText(message)

      // if (error.message) {
      //   setErrorText(Object.values(error.message)[0])
      // } else if (typeof error === "string") {
      //   setErrorText(error)
      // } else {
      //   for (let i in error) {
      //     message.error(`${i}: ${error[i]}`, 5)
      //   }
      // }
      resetError()
    }
  }, [error, resetError])

  function onChange(value: string) {
    setErrorText(null)
    if (!!value && value.length > 5) {
      setIsComplete(true)
      setCode(value)
    } else {
      setIsComplete(false)
      setCode(null)
    }
  }

  function initCodeBlock(collection: HTMLCollection) {
    Array.from(collection).forEach((el) => {
      ;(el as HTMLInputElement).placeholder = "-"
    })
  }

  function handleResendEmail() {
    if (email) {
      setDeadline(Date.now() + 1000 * 60)
      setResendFetching(true)
      if (isConfirmEmail) {
        alert(`resend code ConfirmEmail ${email}`)
      } else {
        checkPassword({ email, navigate })
      }
    } else {
      if (isConfirmEmail) {
        navigate("/login")
      } else {
        navigate("/restore-password")
      }
    }
  }

  const handleConfirmPassword = () => {
    if (code) confirmPassword({ code, navigate })
  }

  function onFinish() {
    setResendFetching(false)
    setDeadline(null)
  }

  return (
    <Layout style={{ backgroundColor: "#e6f2f5", minHeight: "100vh" }}>
      <Spin size="large" spinning={fetching}>
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ minHeight: "100vh" }}
        >
          <LoginHeader />

          <Card className="login-card" style={{ margin: "auto" }}>
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <Title level={2} style={{ marginBottom: 6 }}>
                {" "}
                {t(content.title)}
              </Title>
              <Paragraph type="secondary" style={{ fontSize: 16, margin: 0 }}>
                {t(content.text)} <br />
                <span className="mark-text">{email}</span>
              </Paragraph>
            </Flex>

            <div
              className={clsx("error-text", { "error-text_empty": !errorText })}
            >
              {errorText && t(errorText)}
            </div>
            <ReactCodeInput
              className={`code-block${errorText ? " error" : ""}`}
              fieldWidth={40}
              fieldHeight={40}
              onChange={onChange}
            />
            <div className="verify-btn-block">
              <ButtonComponent
                type="primary"
                size="large"
                htmlType="submit"
                disabled={!isComplete}
                onClick={handleConfirmPassword}
                block
              >
                {t(content.btn)}
              </ButtonComponent>

              <ButtonComponent
                size="large"
                href={`https://mail.${utils.checkMailDomain(email).link}.com`}
                target="_blank"
                rel="noopener noreferrer"
                block
              >
                <img
                  className="btn-icon icon-space"
                  src={
                    Images[
                      utils.checkMailDomain(email).type as keyof typeof Images
                    ]
                  }
                  alt=""
                />
                {t("Go To")} {utils.checkMailDomain(email).name}
              </ButtonComponent>
            </div>

            <div
              className="sign-up-link check-mail"
              style={{ justifyContent: "center" }}
            >
              {resendFetching ? (
                <div className="resend-timer">
                  <Countdown
                    title=""
                    value={deadline || 0}
                    onFinish={onFinish}
                    format="mm:ss"
                    prefix={
                      <span>{t("You will able to resend code in")} </span>
                    }
                  />
                  <Spin />
                </div>
              ) : (
                <span>
                  {`${t("Didn’t receive a code?")} `}
                  <span className="text-link" onClick={handleResendEmail}>
                    {t("Resend Code")}
                  </span>
                </span>
              )}
            </div>
          </Card>

          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    isDesktop: state.nav.isDesktop,
    isMobile: state.nav.isMobile,
    error: state.auth.error,
    fetching: state.auth.fetching,
    userInfo: state.auth.userInfo
  }
}

const mapDispatchToProps = {
  confirmPassword: confirmPasswordThunk,
  checkPassword: checkPasswordThunk,
  resetError: authErrorReset
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(VerifyCodePage)
